import { useState, useRef, useEffect } from 'react';
import { Volume2, Square, Loader2 } from 'lucide-react';
import { ChatMessage, MessagePart } from '../utils/messageHelpers';
import { HotelMetricsWidget } from './HotelMetricsWidget';
import { safeParseHotelMetrics } from '../specs/hotelMetrics';
import { ToolTimeline } from './ToolTimeline';
import { SuggestionActionWidget } from './SuggestionActionWidget';
import type { SuggestionActionWidget as SuggestionActionWidgetType } from './SuggestionActionWidget';
import { SupportTicketComposeWidget } from './SupportTicketComposeWidget';
import type { SupportTicketComposeWidgetSpec } from './SupportTicketComposeWidget';
import { CustomReasoningWidget } from './CustomReasoningWidget';
import { safeParseCustomReasoning } from '../specs/customReasoning';
import { Response } from './Response';
import type { WidgetStatesMap } from '../types/widgetState';

interface AdjustmentOption {
  label?: string;
  description?: string;
  recommended?: boolean;
}

interface AdjustmentOptionsData {
  question: string;
  options?: AdjustmentOption[];
}

function tryParseToolWidget(output: unknown): { kind: "suggestion" | "adjustment"; data: Record<string, unknown> } | null {
  try {
    const result = typeof output === "string" ? JSON.parse(output) : output;
    if (!result?.widget) return null;
    const widget = typeof result.widget === "string" ? JSON.parse(result.widget) : result.widget;
    if (widget?.type === "suggestion-action" && widget?.apiCall) return { kind: "suggestion", data: widget };
    if (widget?.type === "adjustment-options" && widget?.options?.length) return { kind: "adjustment", data: widget };
  } catch {
    return null;
  }
  return null;
}

interface AssistantMessageProps {
  message: ChatMessage;
  onApprove?: (approvalId: string) => Promise<void>;
  onDeny?: (approvalId: string) => Promise<void>;
  isStreaming?: boolean;
  onApplySuccess?: () => void;
  hideThinking?: boolean;
  compact?: boolean;
  expanded?: boolean;
  isExistingConversation?: boolean;
  conversationId?: string;
  widgetStates?: WidgetStatesMap;
  onWidgetActive?: (key: string, info: { conversationId: string; messageId: string; actionType: string; widgetId?: string; partIndex?: number; useNewPersistence?: boolean }) => void;
  onWidgetInactive?: (key: string) => void;
}

/**
 * Assistant Message Component (Production)
 *
 * Displays assistant messages with V1 styling + V2 functionality:
 * - Gray bubble background (#F9FAFB)
 * - Dark text (#1C1C1C)
 * - Rounded with top-left corner cut
 * - Markdown support
 * - Inline widgets (hotel metrics)
 * - Tool approval cards
 */
export function AssistantMessage({
  message,
  onApprove,
  onDeny,
  isStreaming = false,
  onApplySuccess,
  hideThinking = false,
  compact = false,
  expanded = false,
  isExistingConversation = false,
  conversationId,
  widgetStates,
  onWidgetActive,
  onWidgetInactive,
}: AssistantMessageProps) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoadingSpeech, setIsLoadingSpeech] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Cleanup speech on unmount (stop both ElevenLabs audio and browser speech)
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (utteranceRef.current) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Extract text content from message parts for TTS
  const getMessageText = (): string => {
    if (!message.parts) return '';

    return message.parts
      .filter(part => part.type === 'text')
      .map(part => part.text || '')
      .join(' ')
      .trim();
  };

  // Select the best available voice for natural-sounding speech
  const getBestVoice = (): SpeechSynthesisVoice | null => {
    const voices = window.speechSynthesis.getVoices();

    // Priority order: Nicky first, then Samantha, then system default
    const preferredNames = ['Nicky', 'Samantha'];

    for (const name of preferredNames) {
      const voice = voices.find(v =>
        v.name.includes(name) && v.lang.startsWith('en')
      );
      if (voice) return voice;
    }

    // Fallback: system default voice
    const defaultVoice = voices.find(v => v.default);
    if (defaultVoice) return defaultVoice;

    // Last resort: first available voice
    return voices[0] || null;
  };

  // Fallback: read aloud with the browser's built-in (robotic) voice.
  const speakWithBrowser = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utteranceRef.current = utterance;

    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) {
      window.speechSynthesis.addEventListener('voiceschanged', () => {
        utterance.voice = getBestVoice();
      }, { once: true });
    } else {
      utterance.voice = getBestVoice();
    }

    utterance.rate = 0.95;
    utterance.onend = () => {
      setIsSpeaking(false);
      utteranceRef.current = null;
    };
    utterance.onerror = () => {
      setIsSpeaking(false);
      utteranceRef.current = null;
    };

    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    window.speechSynthesis.cancel();
    utteranceRef.current = null;
    setIsSpeaking(false);
  };

  // Read the message aloud in a natural ElevenLabs voice. The audio is proxied
  // through our backend (POST /ai/tts) so the API key stays server-side. If TTS
  // is unavailable (e.g. key not configured -> 503), fall back to the browser voice.
  const handleSpeak = async () => {
    const text = getMessageText();
    if (!text) return;

    if (isSpeaking || isLoadingSpeech) {
      stopSpeaking();
      setIsLoadingSpeech(false);
      return;
    }

    setIsLoadingSpeech(true);
    try {
      const apiBase = (
        process.env.REACT_APP_API_URL || 'https://api.autumnplatform.com'
      ).replace(/\/$/, '');
      const res = await fetch(`${apiBase}/ai/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error(`TTS request failed: ${res.status}`);

      const url = URL.createObjectURL(await res.blob());
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };
      audio.onerror = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };
      setIsLoadingSpeech(false);
      setIsSpeaking(true);
      await audio.play();
    } catch (err) {
      // ElevenLabs unavailable — fall back to the browser voice.
      console.warn('[TTS] ElevenLabs unavailable, using browser voice:', err);
      setIsLoadingSpeech(false);
      speakWithBrowser(text);
    }
  };

  if (!message.parts) return null;

  // Separate parts into 4 categories: tools, reasoning, tool streaming events, content
  const toolParts: MessagePart[] = [];
  const reasoningParts: MessagePart[] = [];
  const toolStreamingParts: MessagePart[] = []; // tool-input-start, reasoning-start, etc.
  const contentParts: MessagePart[] = [];

  message.parts.forEach((part) => {
    // Tool streaming events (show in reasoning UI) - check FIRST
    if (
      part.type === 'tool-input-start' ||
      part.type === 'tool-input-delta' ||
      part.type === 'tool-input-end' ||
      part.type === 'reasoning-start' ||
      part.type === 'reasoning-delta' ||
      part.type === 'reasoning-end' ||
      part.type === 'tool-call' || // Also include initial tool-call events
      part.type === 'step-start' // Include step markers
    ) {
      toolStreamingParts.push(part);
    }
    // Tool-related parts with states — check for embedded widgets in tool outputs
    else if (part.type?.startsWith('tool-')) {
      if (part.state === 'output-available' && part.output) {
        const parsed = tryParseToolWidget(part.output);
        if (parsed?.kind === "suggestion") {
          contentParts.push({ type: 'data-suggestion-action' as MessagePart['type'], data: parsed.data });
        } else if (parsed?.kind === "adjustment") {
          contentParts.push({ type: 'data-adjustment-options' as MessagePart['type'], data: parsed.data });
        } else {
          toolParts.push(part);
        }
      } else {
        toolParts.push(part);
      }
    }
    // Reasoning parts
    else if (part.type === 'data-custom-reasoning') {
      reasoningParts.push(part);
    }
    // Content parts (text, widgets, suggestions)
    else if (
      part.type === 'text' ||
      part.type === 'data-widget' ||
      part.type === 'data-hotel-metrics-data' ||
      part.type === 'data-suggestion-action' ||
      part.type === 'data-support-ticket-compose' ||
      part.type === 'data-adjustment-options'
    ) {
      contentParts.push(part);
    }
    // Ignore other internal events
  });

  // Parse and deduplicate reasoning steps
  const reasoningSteps = reasoningParts
    .map((part) => safeParseCustomReasoning(part.data))
    .filter((step): step is NonNullable<typeof step> => step !== null);

  const uniqueReasoningSteps = reasoningSteps.filter(
    (step, index, self) =>
      index ===
      self.findIndex(
        (s) => s.timestamp === step.timestamp && s.thought === step.thought
      )
  );

  const renderContentPart = (part: MessagePart, idx: number) => {
    // Handle text parts with Response component for better formatting
    if (part.type === 'text') {
      if (!part.text || part.text.trim() === '') return null;
      return (
        <div key={idx} className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
          <Response parseIncompleteMarkdown={isStreaming}>
            {part.text}
          </Response>
        </div>
      );
    }

    // Handle data-hotel-metrics-data parts (AI SDK v6 format)
    // Backend sends type: 'data-hotel-metrics-data', data: {...widget...}
    // Chart type is determined by suggestedChartType field in the data
    if (part.type === 'data-hotel-metrics-data' && part.data) {
      const parseResult = safeParseHotelMetrics(part.data);

      if (parseResult.success) {
        // Determine chart type from suggestedChartType hint
        // "grouped-bar" -> bar chart, "line" -> line chart, default to bar
        const suggestedType = parseResult.data.displayHints?.suggestedChartType;
        const chartType = suggestedType === 'line' ? 'line' : 'bar';

        return (
          <div key={idx} className="mt-2 w-full">
            <HotelMetricsWidget data={parseResult.data} chartType={chartType} />
          </div>
        );
      }

      // Silent failure - widget parsing failed, don't show error to user
      // Error is logged to console for debugging
      return null;
    }

    // Handle data-widget parts (legacy format - deprecated)
    if (part.type === 'data-widget' && part.data) {
      const widgetData = part.data as { type?: string; [key: string]: unknown };

      if (widgetData.type === 'hotel-metrics-data') {
        const parseResult = safeParseHotelMetrics(part.data);

        if (parseResult.success) {
          return (
            <div key={idx} className="mt-2 w-full">
              <HotelMetricsWidget data={parseResult.data} />
            </div>
          );
        }

        return (
          <div key={idx} className="mt-2 text-footnote italic text-content-tertiary">
            [Metrics data received but cannot be rendered]
          </div>
        );
      }

      // Silent failure - unknown widget type, don't show error to user
      return null;
    }

    // Handle support-ticket compose widgets (new email-style composer)
    if (part.type === 'data-support-ticket-compose' && part.data) {
      try {
        const ticketData = part.data as SupportTicketComposeWidgetSpec & {
          status?: string;
          _messageId?: string;
          _partIndex?: number;
        };
        if (ticketData.type !== 'support-ticket-compose' || !ticketData.apiCall) {
          return null;
        }

        const widgetId = ticketData.widgetId;
        const useNewSystem = !!widgetId && !!widgetStates;

        let initialState: 'idle' | 'success' | 'dismissed' = 'idle';
        let initialPersistedStatus: 'applied' | 'dismissed' | undefined;

        if (isExistingConversation) {
          if (useNewSystem) {
            const serverState = widgetStates![widgetId!];
            if (serverState?.status === 'applied') {
              initialState = 'success';
              initialPersistedStatus = 'applied';
            } else if (serverState?.status === 'dismissed') {
              initialState = 'dismissed';
              initialPersistedStatus = 'dismissed';
            }
          } else {
            const restoredStatus = ticketData.status;
            if (restoredStatus === 'applied') {
              initialState = 'success';
              initialPersistedStatus = 'applied';
            } else if (restoredStatus === 'dismissed') {
              initialState = 'dismissed';
              initialPersistedStatus = 'dismissed';
            }
          }
        }

        return (
          <div key={idx} className="mt-2 w-full">
            <SupportTicketComposeWidget
              widget={ticketData}
              onDismiss={() => {}}
              onSendSuccess={onApplySuccess}
              conversationId={conversationId}
              messageId={ticketData._messageId ?? message.id}
              partIndex={ticketData._partIndex ?? idx}
              initialState={initialState}
              initialPersistedStatus={initialPersistedStatus}
              widgetStates={widgetStates}
              onWidgetActive={onWidgetActive}
              onWidgetInactive={onWidgetInactive}
              useNewPersistence={useNewSystem}
            />
          </div>
        );
      } catch {
        return null;
      }
    }

    // Handle AI suggestion action widgets
    if (part.type === 'data-suggestion-action' && part.data) {
      try {
        const suggestionData = part.data as SuggestionActionWidgetType & { widgetId?: string; status?: string; _messageId?: string; _partIndex?: number };
        if (suggestionData.type === 'suggestion-action' && suggestionData.apiCall) {
          const widgetId = suggestionData.widgetId as string | undefined;
          const useNewSystem = !!widgetId && !!widgetStates;

          let initialState: 'idle' | 'success' | 'dismissed' = 'idle';
          let initialPersistedStatus: 'applied' | 'adjusted' | 'dismissed' | undefined;

          if (isExistingConversation) {
            if (useNewSystem) {
              // New system: look up in widgetStates map (main chat)
              const serverState = widgetStates![widgetId!];
              if (serverState?.status === 'applied' || serverState?.status === 'adjusted') {
                initialState = 'success';
                initialPersistedStatus = serverState.status;
              } else if (serverState?.status === 'dismissed') {
                initialState = 'dismissed';
                initialPersistedStatus = 'dismissed';
              }
              // No state → pending → 'idle'
            } else {
              // Old system: pricing agent server-side p.status
              const restoredStatus = suggestionData.status as string | undefined;
              if (restoredStatus === 'applied') {
                initialState = 'success';
                initialPersistedStatus = 'applied';
              } else if (restoredStatus === 'dismissed') {
                initialState = 'dismissed';
                initialPersistedStatus = 'dismissed';
              }
            }
          }

          return (
            <div key={idx} className="mt-2 w-full">
              <SuggestionActionWidget
                widget={suggestionData}
                onDismiss={() => {}}
                onApplySuccess={onApplySuccess}
                compact={compact}
                expanded={expanded}
                initialState={initialState}
                initialPersistedStatus={initialPersistedStatus}
                conversationId={conversationId}
                messageId={suggestionData._messageId ?? message.id}
                partIndex={suggestionData._partIndex ?? idx}
                onWidgetActive={onWidgetActive}
                onWidgetInactive={onWidgetInactive}
                useNewPersistence={useNewSystem}
              />
            </div>
          );
        }
        return null;
      } catch {
        return null;
      }
    }

    // Handle adjustment options widgets (pricing agent multi-choice)
    if (part.type === 'data-adjustment-options' && part.data) {
      const adjData = part.data as AdjustmentOptionsData;
      return (
        <div key={idx} className="mt-2 w-full">
          <div className="bg-surface-primary border border-border-primary rounded-lg shadow-sm">
            <div className="px-3 py-2 border-b border-border-secondary">
              <h3 className="text-xs font-semibold text-content-primary">{adjData.question}</h3>
            </div>
            <div className="px-3 py-2 space-y-1.5">
              {adjData.options?.map((opt: AdjustmentOption) => {
                const isRecommended = opt.recommended ?? opt.label?.includes("(Recommended)");
                const displayLabel = opt.label?.replace(/\s*\(Recommended\)/, "");
                return (
                  <div
                    key={opt.label}
                    className={`px-3 py-2 rounded-md border text-left text-xs ${
                      isRecommended
                        ? "border-brand-primary/30 bg-brand-primary/5"
                        : "border-border-primary bg-white"
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-content-primary">{displayLabel}</span>
                      {isRecommended && (
                        <span className="text-[9px] font-medium text-brand-primary">Recommended</span>
                      )}
                    </div>
                    {opt.description && (
                      <p className="mt-0.5 text-content-secondary text-[10px]">{opt.description}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderedContent = contentParts.map(renderContentPart);
  const filteredContent = renderedContent.filter(
    (item): item is JSX.Element => item !== null
  );

  // Filter tool parts to only show those in renderable states
  // This is separate from the earlier collection to ensure Message #2 isn't skipped
  // ONLY show approval-requested and results - hide intermediate streaming states
  const renderableToolParts = toolParts.filter(
    (part) =>
      part.state === 'approval-requested' ||
      part.state === 'output-available' ||
      part.state === 'output-error'
  );

  // If no content to show, return null
  // BUT: if actively streaming, show the message (with "Thinking..." widget)
  if (
    filteredContent.length === 0 &&
    renderableToolParts.length === 0 &&
    uniqueReasoningSteps.length === 0 &&
    toolStreamingParts.length === 0 &&
    !isStreaming
  ) {
    return null;
  }

  // Check if embedded widget should render (only when there's actual visible content)
  const shouldShowEmbeddedWidget =
    uniqueReasoningSteps.length > 0 ||
    renderableToolParts.length > 0 ||
    // Only show for tool events if there are ACTUAL tool names/data to display
    (toolStreamingParts.length > 0 && toolStreamingParts.some(
      part => (part.type === 'tool-call' || part.type === 'tool-input-start') && (part.toolName || part.tool)
    ));

  // Check if there's text content to speak
  const hasTextContent = contentParts.some(
    part => part.type === 'text' && part.text?.trim()
  );

  // Render in chronological order: Reasoning (with embedded tools) → Text/Widgets
  return (
    <div className="self-stretch justify-start items-start flex max-w-full min-w-0 flex-col gap-3">
      {/* 1. Reasoning + Tool Streaming + Tool Execution - renders FIRST (all-in-one) */}
      {/* Show widget only when there's actual visible content to prevent flickering */}
      {shouldShowEmbeddedWidget && !hideThinking && (
        <CustomReasoningWidget
          reasoningSteps={uniqueReasoningSteps}
          toolEvents={toolStreamingParts}
          toolParts={renderableToolParts}
          onApprove={onApprove}
          onDeny={onDeny}
          isStreaming={isStreaming}
        />
      )}

      {/* 2. Content segments - text bubbles interleaved with widgets */}
      {(() => {
        const segments: { kind: 'text' | 'widget'; rendered: React.ReactNode[] }[] = [];
        contentParts.forEach((part, i) => {
          const rendered = filteredContent[i];
          if (!rendered) return;
          const kind = part.type === 'text' ? 'text' as const : 'widget' as const;
          const last = segments[segments.length - 1];
          if (last && last.kind === kind) {
            last.rendered.push(rendered);
          } else {
            segments.push({ kind, rendered: [rendered] });
          }
        });

        const lastTextSegmentIdx = segments.reduce((acc, seg, i) => seg.kind === 'text' ? i : acc, -1);

        return segments.map((segment, segIdx) => {
          if (segment.kind === 'widget') {
            return (
              <div key={`seg-${segIdx}`} className="w-full overflow-hidden">{segment.rendered}</div>
            );
          }
          const isLastTextSegment = segIdx === lastTextSegmentIdx;
          return (
            <div key={`seg-${segIdx}`} className="relative group max-w-full">
              {/* Assistant bubble — kit cream-soft tint w/ flattened tl
                  corner (mirror of the user bubble), kit body font, ink
                  text. Removed the legacy `[&_*]:!text-[11px]` override
                  that crushed markdown content to 11px regardless of
                  element (headings, code, lists were all squished). */}
              <div
                className="custom-message w-fit max-w-full break-words px-3.5 py-2.5 rounded-2xl rounded-tl-md"
                style={{
                  background: "var(--d-cream-soft)",
                  border: "1px solid var(--d-rule-soft)",
                  color: "var(--d-ink)",
                  fontFamily: "var(--d-font-body)",
                  fontSize: 13.5,
                  lineHeight: 1.55,
                  letterSpacing: "-0.003em",
                }}
              >
                {segment.rendered}
              </div>

              {isLastTextSegment && !isStreaming && hasTextContent && (
                <button
                  onClick={handleSpeak}
                  className="absolute -bottom-1 -right-1 p-1.5 rounded-full transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
                  style={{
                    background: "var(--d-surface)",
                    border: "1px solid var(--d-rule)",
                    boxShadow: "var(--d-shadow-card)",
                    color: "var(--d-ink-muted)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "var(--d-accent-soft)";
                    e.currentTarget.style.background = "var(--d-accent-tint)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "var(--d-rule)";
                    e.currentTarget.style.background = "var(--d-surface)";
                  }}
                  aria-label={isSpeaking || isLoadingSpeech ? "Stop speaking" : "Read message aloud"}
                  title={isSpeaking || isLoadingSpeech ? "Stop speaking" : "Read message aloud"}
                >
                  {isLoadingSpeech ? (
                    <Loader2 className="w-3 h-3 animate-spin" style={{ color: "var(--d-ink-muted)" }} />
                  ) : isSpeaking ? (
                    <Square className="w-3 h-3" fill="currentColor" style={{ color: "var(--d-accent-deep)" }} />
                  ) : (
                    <Volume2 className="w-3 h-3" />
                  )}
                </button>
              )}
            </div>
          );
        });
      })()}
    </div>
  );
}
